import { useMemo, useState } from 'react';
import {
  UserSearch, Search, Building2, Lock, ArrowDownWideNarrow, ArrowUpWideNarrow,
  Trophy, CalendarDays, TrendingUp, Wallet, Percent, Gauge, Users2, MapPin,
  LayoutGrid, Repeat2, PieChart, Info,
} from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/context/TenantContext';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';
import { RecruiterProfile } from '@/components/recruiters/RecruiterProfile';
import { RosterPanel } from '@/components/recruiters/RosterPanel';
import { RecruiterPareto } from '@/components/recruiters/RecruiterPareto';

const ALL = '__ALL__';
const eur0 = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

// Metrics shown on each card (and available for sorting).
const METRICS = [
  { key: 'bs_reel', label: 'BS validés', icon: TrendingUp, fmt: (v) => fmtInt(v) },
  { key: 'taux_transfo', label: 'Transformation', icon: Percent, fmt: (v) => fmtPct(v) },
  { key: 'bs_par_jour', label: 'BS / jour', icon: Gauge, fmt: (v) => fmtNum(v, 2) },
  { key: 'don_moyen', label: 'Don moyen', icon: Wallet, fmt: (v) => fmtEur(v) },
  { key: 'montant', label: 'Montant récurrent', icon: Wallet, fmt: (v) => eur0(v) },
  { key: 'jours_actifs', label: 'Jours actifs', icon: CalendarDays, fmt: (v) => fmtInt(v) },
];
// Extra sortable metrics not shown as tiles.
const SORTS = [
  ...METRICS,
  { key: 'bs_rue', label: 'BS signés' },
  { key: 'age_moyen', label: 'Âge moyen' },
  { key: 'nb_spots', label: 'Spots' },
  { key: 'key', label: 'Nom (A→Z)' },
];
const MEDALS = ['#EAB308', '#94A3B8', '#B45309'];

const HeaderKpi = ({ label, value, sub }) => (
  <div className="rounded-2xl border bg-card p-4 card-shadow" data-testid="recruiters-header-kpi">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold tabular text-foreground">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

const RankBadge = ({ n }) => (
  n <= 3
    ? <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: MEDALS[n - 1] }}>{n}</span>
    : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground tabular">{n}</span>
);

const RecruiterCard = ({ r, pos, sortKey, onOpen }) => (
  <button type="button" onClick={onOpen} data-testid="recruiter-card"
    className="group flex w-full flex-col rounded-2xl border bg-card p-4 text-left card-shadow transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-[color:var(--tenant-accent-300)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)]">
    <div className="flex items-start gap-3">
      <RankBadge n={pos} />
      <div className="min-w-0 flex-1">
        <p className="font-display text-[15px] font-semibold leading-tight text-foreground break-words">{r.key}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {r.code && <span className="font-mono text-[10px] text-muted-foreground">{r.code}</span>}
          {(r.association_names || []).map((a) => (
            <span key={a} className="inline-flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              <Building2 className="h-2.5 w-2.5" />{a}
            </span>
          ))}
        </div>
      </div>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-x-3 gap-y-3 border-t pt-3">
      {METRICS.map((m) => {
        const active = m.key === sortKey;
        return (
          <div key={m.key} className={active ? 'rounded-lg accent-bg-100 px-2 py-1 -mx-1' : 'px-1'}>
            <p className={`text-[10px] leading-tight ${active ? 'accent-text font-medium' : 'text-muted-foreground'}`}>{m.label}</p>
            <p className={`mt-0.5 font-display text-base font-semibold tabular leading-none ${active ? 'accent-text' : 'text-foreground'}`}>{m.fmt(r[m.key])}</p>
          </div>
        );
      })}
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1"><Users2 className="h-3 w-3" />{fmtInt(r.bs_rue)} signés</span>
      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{fmtInt(r.nb_spots)} spots</span>
      <span>Âge moy. {fmtNum(r.age_moyen, 1)}</span>
    </div>
  </button>
);

// --- Classement (leaderboard) view -----------------------------------------
const ClassementView = ({ body, onSelect }) => {
  const [minBs, setMinBs] = useState('20');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('bs_reel');
  const [sortDir, setSortDir] = useState('desc');

  const reqBody = useMemo(() => ({ ...body, min_bs: Number(minBs) }), [body, minBs]);
  const { data, isLoading } = usePost('/admin/recruiters/ranking', reqBody);

  const all = data?.recruiters || [];
  const glob = data?.global || {};

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? all.filter((r) => r.key.toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q))
      : all.slice();
    list.sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [all, search, sortKey, sortDir]);

  const sortLabel = (SORTS.find((s) => s.key === sortKey) || {}).label;

  return (
    <div className="space-y-6">
      {/* Header KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <HeaderKpi label="Recruteurs suivis" value={fmtInt(glob.recruteurs || 0)} sub={data ? `${fmtInt(data.total)} au total` : null} />
        <HeaderKpi label="BS validés (cumul)" value={fmtInt(glob.bs_reel || 0)} sub={`${fmtInt(glob.bs_rue || 0)} signés`} />
        <HeaderKpi label="Transfo. moyenne" value={fmtPct(glob.taux_transfo || 0)} sub={`invalidation ${fmtPct(glob.taux_invalidation || 0)}`} />
        <HeaderKpi label="Montant mensuel récurrent" value={eur0(glob.montant || 0)} sub={`don moyen ${fmtEur(glob.don_moyen || 0)}`} />
      </div>

      {/* Threshold notice: explains why KPIs read 0 when nobody clears the filter */}
      {data && (glob.recruteurs || 0) === 0 && (data.total || 0) > 0 && Number(minBs) > 0 && (
        <div className="flex flex-col gap-2 rounded-2xl border border-dashed bg-muted/30 p-3 text-xs sm:flex-row sm:items-center sm:justify-between" data-testid="recruiters-threshold-notice">
          <span className="flex items-start gap-2 text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
            <span className="break-words">
              Sur cette période, aucun des <strong className="text-foreground">{fmtInt(data.total)}</strong> recruteurs
              n'atteint le seuil <strong className="text-foreground">≥ {minBs} BS signés</strong> — les indicateurs ci-dessus sont donc à 0.
            </span>
          </span>
          <Button variant="outline" size="sm" className="shrink-0 rounded-xl" onClick={() => setMinBs('0')} data-testid="recruiters-show-all">
            Afficher tous les recruteurs
          </Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-3 rounded-2xl border bg-card p-3 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="relative min-w-0 flex-1 lg:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un recruteur…"
            className="rounded-xl pl-9" data-testid="recruiter-search" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger className="h-9 w-[190px] rounded-xl" data-testid="recruiter-sort">
                <span className="text-muted-foreground">Trier :</span><SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 rounded-xl"
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              data-testid="recruiter-sort-dir" title={sortDir === 'asc' ? 'Croissant' : 'Décroissant'}>
              {sortDir === 'asc' ? <ArrowUpWideNarrow className="h-4 w-4" /> : <ArrowDownWideNarrow className="h-4 w-4" />}
            </Button>
          </div>

          <Select value={minBs} onValueChange={setMinBs}>
            <SelectTrigger className="h-9 w-[150px] rounded-xl" data-testid="recruiter-minbs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tous</SelectItem>
              <SelectItem value="20">≥ 20 BS signés</SelectItem>
              <SelectItem value="50">≥ 50 BS signés</SelectItem>
              <SelectItem value="100">≥ 100 BS signés</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isLoading && rows.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="recruiters-count">
          {rows.length} recruteur(s) · triés par <strong className="text-foreground">{sortLabel}</strong> · cliquez une carte pour la fiche détaillée
        </p>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} height="h-44" />)}
        </div>
      ) : rows.length === 0 ? (
        Number(minBs) > 0 && (data?.total || 0) > 0 ? (
          <EmptyState title="Aucun recruteur au-dessus du seuil"
            body={`Aucun des ${fmtInt(data.total)} recruteurs de la période n'atteint ≥ ${minBs} BS signés. Affichez tous les recruteurs ou élargissez la période.`}
            onClear={() => setMinBs('0')} actionLabel="Afficher tous les recruteurs" />
        ) : (
          <EmptyState title="Aucun recruteur pour ces filtres"
            body="Élargissez la période, changez d'association ou abaissez le seuil de pertinence." />
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="recruiters-grid">
          {rows.map((r, i) => (
            <RecruiterCard key={r.key} r={r} pos={i + 1} sortKey={sortKey} onOpen={() => onSelect(r.key)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function Recruteurs() {
  const { body, options } = useFilters();
  const { isCampaignManager } = useAuth();
  const { associations: tenantAssocs } = useTenant();
  const [view, setView] = useState('classement');
  const [assocFilter, setAssocFilter] = useState(ALL);
  const [selected, setSelected] = useState(null);

  // Effective body: page-level association scope overrides the tenant switcher.
  const fBody = useMemo(() => ({ ...body, association: assocFilter }), [body, assocFilter]);
  // Admins get the full list (aggregated options); campaign managers get only
  // the associations within their own perimeter.
  const associations = isCampaignManager ? (tenantAssocs || []) : (options?.associations || []);

  return (
    <div className="space-y-6">
      <PageHeader title="Recruteurs" icon={UserSearch}
        subtitle={isCampaignManager
          ? "Performance individuelle des recruteurs de votre équipe"
          : "Performance individuelle des recruteurs de donateurs — vue interne"} />

      {!isCampaignManager && (
        <div className="flex items-start gap-2 rounded-2xl border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground" data-testid="recruiters-internal-note">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
          <span>Espace <strong className="text-foreground">interne SHARITI</strong> — suivi individuel des recruteurs. Ces données ne sont jamais exposées côté association.</span>
        </div>
      )}

      {/* View toggle + shared association scope */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="rounded-xl" data-testid="recruiters-view-toggle">
            <TabsTrigger value="classement" className="gap-1.5 rounded-lg" data-testid="recruiters-view-classement">
              <LayoutGrid className="h-3.5 w-3.5" /> Classement
            </TabsTrigger>
            <TabsTrigger value="vivier" className="gap-1.5 rounded-lg" data-testid="recruiters-view-vivier">
              <Repeat2 className="h-3.5 w-3.5" /> Vivier
            </TabsTrigger>
            <TabsTrigger value="pareto" className="gap-1.5 rounded-lg" data-testid="recruiters-view-pareto">
              <PieChart className="h-3.5 w-3.5" /> Pareto 20/80
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <span className="hidden text-xs text-muted-foreground sm:inline">Périmètre</span>
          <Select value={assocFilter} onValueChange={setAssocFilter}>
            <SelectTrigger className="h-9 w-[210px] rounded-xl bg-card" data-testid="recruiter-assoc-filter">
              <Building2 className="mr-1 h-4 w-4 text-muted-foreground" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{isCampaignManager ? 'Toutes mes associations' : 'Toutes les associations'}</SelectItem>
              {associations.map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name || a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {view === 'classement' && <ClassementView body={fBody} onSelect={setSelected} />}
      {view === 'vivier' && <RosterPanel body={fBody} onSelect={setSelected} />}
      {view === 'pareto' && <RecruiterPareto body={fBody} onSelect={setSelected} />}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="recruiter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl">
              <Trophy className="h-5 w-5 accent-text" />{selected}
            </DialogTitle>
          </DialogHeader>
          {selected && <RecruiterProfile recruiter={selected} body={fBody} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
